import { useEffect, useRef, useState } from "react";

/**
 * OtpInput — segmented OTP field with auto-submit on the last digit.
 *
 * Behaviour:
 *   - N boxes (default 6). Focus advances as the user types.
 *   - Paste of the full code fills every box at once.
 *   - Backspace on an empty box moves focus back and clears the previous one.
 *   - onComplete fires as soon as the last digit is entered — the parent form
 *     should not need a Submit button.
 *   - Loading disables the whole row. Error paints boxes red + shakes once.
 *
 * Reuse: OrganizerSecurity, OrganizerSetup, NailEventLanding — same visual
 * language everywhere so users on either flow see the same interaction.
 */
export default function OtpInput({
  length = 6,
  value = "",
  onChange,
  onComplete,
  loading = false,
  error = false,
  autoFocus = true,
  testIdPrefix = "otp",
  ariaLabel = "OTP",
}) {
  const [local, setLocal] = useState(value || "");
  const [shake, setShake] = useState(false);
  const refs = useRef([]);
  const lastFiredRef = useRef("");

  // Sync external `value` prop (e.g. parent clears after failure)
  useEffect(() => { setLocal(value || ""); }, [value]);

  // Autofocus first box
  useEffect(() => {
    if (autoFocus && !loading) refs.current[0]?.focus();
  }, [autoFocus, loading]);

  // Shake on error prop transition
  useEffect(() => {
    if (!error) return;
    setShake(true);
    const t = setTimeout(() => setShake(false), 500);
    return () => clearTimeout(t);
  }, [error]);

  const commit = (next) => {
    const trimmed = next.replace(/\D/g, "").slice(0, length);
    setLocal(trimmed);
    onChange?.(trimmed);
    // Auto-fire on last digit — only once per unique complete code so a
    // rapid re-fire from repaint doesn't double-submit.
    if (trimmed.length === length && trimmed !== lastFiredRef.current) {
      lastFiredRef.current = trimmed;
      // Slight defer so the last box paints filled before the submit spinner.
      setTimeout(() => onComplete?.(trimmed), 0);
    }
    // If user cleared, allow re-fire later
    if (trimmed.length < length) lastFiredRef.current = "";
  };

  const handleInput = (i, e) => {
    const raw = e.target.value;
    // Paste of full code lands on box i
    if (raw.length > 1) {
      commit(raw);
      const filled = raw.replace(/\D/g, "").slice(0, length);
      const nextIdx = Math.min(filled.length, length - 1);
      refs.current[nextIdx]?.focus();
      refs.current[nextIdx]?.select?.();
      return;
    }
    const digit = raw.replace(/\D/g, "").slice(-1);
    const arr = local.padEnd(length, " ").split("");
    if (digit) {
      arr[i] = digit;
      const next = arr.join("").replace(/ /g, "").slice(0, length);
      commit(next);
      if (i < length - 1) refs.current[i + 1]?.focus();
    } else {
      // Empty input event → treat as clearing this box
      arr[i] = " ";
      commit(arr.join("").replace(/ /g, ""));
    }
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (!local[i] && i > 0) {
        // Backspace on empty box → clear previous and step back
        e.preventDefault();
        const arr = local.split("");
        arr[i - 1] = "";
        commit(arr.join(""));
        refs.current[i - 1]?.focus();
      }
      return;
    }
    if (e.key === "ArrowLeft" && i > 0) { e.preventDefault(); refs.current[i - 1]?.focus(); return; }
    if (e.key === "ArrowRight" && i < length - 1) { e.preventDefault(); refs.current[i + 1]?.focus(); return; }
  };

  const boxes = Array.from({ length }, (_, i) => local[i] || "");

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={testIdPrefix}
      className={`inline-flex items-center gap-2 sm:gap-3 ${shake ? "otp-shake" : ""}`}
    >
      {boxes.map((v, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={v}
          disabled={loading}
          onChange={(e) => handleInput(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          data-testid={`${testIdPrefix}-box-${i}`}
          aria-label={`Digit ${i + 1} of ${length}`}
          className={[
            "w-11 h-14 sm:w-12 sm:h-16 text-center font-mono text-2xl sm:text-3xl rounded-xl border-2 transition-all outline-none",
            error
              ? "border-rose-400 bg-rose-50 text-rose-900"
              : v
                ? "border-emerald2 bg-white text-charcoal"
                : "border-warmgray-300 bg-white text-charcoal focus:border-emerald2",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          ].join(" ")}
        />
      ))}
      <style>{`
        .otp-shake { animation: otp-shake 0.4s cubic-bezier(.36,.07,.19,.97) both; }
        @keyframes otp-shake {
          10%, 90% { transform: translateX(-2px); }
          20%, 80% { transform: translateX(3px); }
          30%, 50%, 70% { transform: translateX(-5px); }
          40%, 60% { transform: translateX(5px); }
        }
      `}</style>
    </div>
  );
}
